import { registerTermUIElement, type HtmlAction } from "../src";

registerTermUIElement();

const term = document.querySelector<HTMLElement>("#term");
const jsCounter = document.querySelector<HTMLElement>("#js-counter");
const adapterZone = document.querySelector<HTMLElement>("#adapter-zone");
const status = document.querySelector<HTMLDivElement>("#status");

if (!term || !jsCounter || !adapterZone || !status) {
  throw new Error("Missing required agnostic demo nodes");
}

let jsCount = 0;
let adapterCount = 0;
let adapterName = "alpha";
let adapterProfile = "balanced";
let adapterEnabled = true;
let adapterNotes = "all systems nominal";
let adapterAdvancedOpen = false;
let adapterWidgetCount = 1;
let mode: "dark" | "light" = "dark";

if (!customElements.get("x-open-widget")) {
  class OpenWidget extends HTMLElement {
    private readonly button: HTMLButtonElement;
    private count = 0;

    constructor() {
      super();
      const shadow = this.attachShadow({ mode: "open" });
      this.button = document.createElement("button");
      this.button.type = "button";
      this.button.addEventListener("click", () => {
        this.count += 1;
        this.setAttribute("data-count", String(this.count));
        this.dispatchEvent(
          new CustomEvent("open-widget-change", {
            detail: { count: this.count },
            bubbles: true,
            composed: true,
          }),
        );
        this.render();
      });
      shadow.appendChild(this.button);
    }

    connectedCallback(): void {
      const raw = this.getAttribute("data-count");
      const parsed = raw ? Number.parseInt(raw, 10) : 0;
      this.count = Number.isFinite(parsed) ? parsed : 0;
      this.render();
    }

    private render(): void {
      this.button.textContent = `Open widget +1 (${this.count})`;
    }
  }

  customElements.define("x-open-widget", OpenWidget);
}

function setStatus(message: string): void {
  status.innerHTML = message;
}

function syncJsCounter(): void {
  jsCounter.textContent = `JS count: ${jsCount}`;
}

function renderAdapterZone(): void {
  adapterZone.innerHTML = `
    <x-panel style="display: grid; gap: 1rem;">
      <h3>Adapter Zone (unknown wrappers + native controls)</h3>
      <p>Adapter count: ${adapterCount}</p>
      <p>Mode: ${mode}</p>
      <x-controls style="display: grid; gap: 0.6rem;">
        <input action="adapter-name" name="adapter-name" placeholder="adapter name">
        <input type="checkbox" action="adapter-enabled" name="adapter-enabled">
        <select action="adapter-profile" name="adapter-profile">
          <option value="fast">fast</option>
          <option value="balanced">balanced</option>
          <option value="safe">safe</option>
        </select>
        <textarea action="adapter-notes" name="adapter-notes" placeholder="notes"></textarea>
        <details action="adapter-advanced">
          <summary>advanced settings</summary>
          <p>Last profile: ${adapterProfile}</p>
        </details>
        <x-open-widget data-count="${adapterWidgetCount}"></x-open-widget>
      </x-controls>
      <x-controls style="display: flex; gap: 0.7rem;">
        <button action="adapter-inc">Adapter +1</button>
        <button action="adapter-reset">Adapter reset</button>
        <button action="adapter-mode">Toggle mode</button>
      </x-controls>
    </x-panel>
  `;

  const nameInput = adapterZone.querySelector<HTMLInputElement>('input[name="adapter-name"]')!;
  nameInput.value = adapterName;

  const enabledInput = adapterZone.querySelector<HTMLInputElement>('input[name="adapter-enabled"]')!;
  enabledInput.checked = adapterEnabled;

  const profileSelect = adapterZone.querySelector<HTMLSelectElement>('select[name="adapter-profile"]')!;
  profileSelect.value = adapterProfile;

  const notesArea = adapterZone.querySelector<HTMLTextAreaElement>('textarea[name="adapter-notes"]')!;
  notesArea.value = adapterNotes;

  const advancedDetails = adapterZone.querySelector<HTMLDetailsElement>("details")!;
  advancedDetails.open = adapterAdvancedOpen;
}

function describe(action: HtmlAction): string {
  if (action.type === "link") return `link (${action.href ?? action.text})`;
  return `action (${action.id ?? action.text})`;
}

term.addEventListener("termui-action", (event) => {
  const action = (event as CustomEvent<HtmlAction>).detail;
  if (action.type !== "action") {
    setStatus(`Last action: <strong>${describe(action)}</strong>`);
    return;
  }

  switch (action.id) {
    case "js-inc":
      jsCount += 1;
      syncJsCounter();
      break;
    case "js-dec":
      jsCount -= 1;
      syncJsCounter();
      break;
    case "adapter-inc":
      adapterCount += 1;
      renderAdapterZone();
      break;
    case "adapter-reset":
      adapterCount = 0;
      renderAdapterZone();
      break;
    case "adapter-mode":
      mode = mode === "dark" ? "light" : "dark";
      term.setAttribute("mode", mode);
      renderAdapterZone();
      break;
    default:
      break;
  }

  setStatus(`Last action: <strong>${describe(action)}</strong>`);
});

adapterZone.addEventListener("input", (event) => {
  const target = event.target;
  if (target instanceof HTMLInputElement) {
    if (target.name === "adapter-name") {
      adapterName = target.value;
      renderAdapterZone();
      return;
    }
  }

  if (target instanceof HTMLTextAreaElement && target.name === "adapter-notes") {
    adapterNotes = target.value;
    renderAdapterZone();
  }
});

adapterZone.addEventListener("change", (event) => {
  const target = event.target;
  if (target instanceof HTMLInputElement && target.name === "adapter-enabled") {
    adapterEnabled = target.checked;
    renderAdapterZone();
    return;
  }
  if (target instanceof HTMLSelectElement && target.name === "adapter-profile") {
    adapterProfile = target.value;
    renderAdapterZone();
  }
});

adapterZone.addEventListener("toggle", (event) => {
  const target = event.target;
  if (target instanceof HTMLDetailsElement) {
    adapterAdvancedOpen = target.open;
    renderAdapterZone();
  }
});

adapterZone.addEventListener("open-widget-change", (event) => {
  const detail = (event as CustomEvent<{ count: number }>).detail;
  adapterWidgetCount = detail.count;
  renderAdapterZone();
});

syncJsCounter();
renderAdapterZone();
